// DayCalendar — Bottom sheet selector de día
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { COLORS, MONTHS } from './designSystem';
import { daysWithEvents } from './EventsService';

const WD = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

export const DayCalendar = ({ onSelect }) => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState(new Date(today));
  const [withEvents, setWithEvents] = useState(new Set());

  useEffect(() => {
    (async () => { setWithEvents(await daysWithEvents()); })();
  }, []);

  const grid = useMemo(() => {
    const firstDay = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = (firstDay.getDay() + 6) % 7;      // lunes = 0
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const cells = [];
    const prevMonthDays = new Date(cursor.getFullYear(), cursor.getMonth(), 0).getDate();
    for (let i = 0; i < start; i++) cells.push({ day: prevMonthDays - start + 1 + i, mute: true });
    for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d, mute: false });
    while (cells.length % 7 !== 0) cells.push({ day: cells.length - start - daysInMonth + 1, mute: true, next: true });
    return cells;
  }, [cursor]);

  const key = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  return (
    <div data-testid="day-calendar">
      <div className="flex gap-2 mb-5 overflow-x-auto no-scrollbar">
        {[['Hoy', 0], ['Mañana', 1], ['Este finde', 'weekend']].map(([label, offset]) => (
          <button key={label} onClick={() => {
            if (offset === 'weekend') {
              const d = new Date(today); const dow = (d.getDay() + 6) % 7; d.setDate(d.getDate() + ((5 - dow + 7) % 7));
              setSelected(d); onSelect(d);
            } else {
              const d = new Date(today); d.setDate(d.getDate() + offset); setSelected(d); onSelect(d);
            }
          }} className="px-3.5 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap" style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.borderStrong}`, color: COLORS.text }}>{label}</button>
        ))}
      </div>
      <div className="flex items-center justify-between mb-3 px-1">
        <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="w-9 h-9 flex items-center justify-center rounded-full" style={{ backgroundColor: COLORS.surface }}><ChevronLeft size={18} color={COLORS.text} /></button>
        <h3 className="text-white font-semibold capitalize">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</h3>
        <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="w-9 h-9 flex items-center justify-center rounded-full" style={{ backgroundColor: COLORS.surface }}><ChevronRight size={18} color={COLORS.text} /></button>
      </div>
      <div className="grid grid-cols-7 gap-y-1 text-center mb-1">
        {WD.map((w) => (<div key={w} className="text-xs font-semibold" style={{ color: COLORS.textMuted }}>{w}</div>))}
      </div>
      <div className="grid grid-cols-7 gap-y-1 text-center">
        {grid.map((c, i) => {
          const y = cursor.getFullYear() + (c.next && c.day < 15 ? (cursor.getMonth() === 11 ? 1 : 0) : 0);
          const m = c.next && c.day < 15 ? (cursor.getMonth() + 1) % 12 : (c.mute && c.day > 15 ? (cursor.getMonth() - 1 + 12) % 12 : cursor.getMonth());
          const k = key(y, m, c.day);
          const dt = new Date(y, m, c.day);
          const isPast = dt < today;
          const isSelected = selected && dt.toDateString() === selected.toDateString();
          const hasEvent = withEvents.has(k);
          const faded = c.mute || isPast;
          return (
            <button key={i} disabled={faded} onClick={() => { setSelected(dt); onSelect(dt); }} className="h-11 flex flex-col items-center justify-center relative" data-testid={`day-cell-${k}`}>
              <div className={`w-9 h-9 flex items-center justify-center rounded-xl text-sm ${isSelected ? 'font-bold' : 'font-semibold'}`} style={{ backgroundColor: isSelected ? COLORS.primary : 'transparent', color: isSelected ? '#fff' : (faded ? '#3a3a40' : COLORS.text) }}>
                {c.day}
              </div>
              {hasEvent && !isSelected && <span className="absolute bottom-1 w-1 h-1 rounded-full" style={{ backgroundColor: COLORS.primary }} />}
              {hasEvent && isSelected && <span className="absolute bottom-1 w-1 h-1 rounded-full" style={{ backgroundColor: '#fff' }} />}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2 mt-4 text-xs" style={{ color: COLORS.textMuted }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: COLORS.primary }} /> Los días con punto tienen eventos
      </div>
    </div>
  );
};
